import {Children,createContext,isValidElement,useContext,useEffect,useState,type HTMLAttributes,type ReactNode} from 'react';
import {createPortal} from 'react-dom';
import {useCopy,useUi} from '@platform/ui/config';
import './Notice.css';
import {resolveNotification} from './notificationEngine';

export type NoticeTone='info'|'success'|'warning'|'error';
// Only explicit transient feedback uses the room viewport layer. Form and
// draw notices stay beside the content they describe.
export const NoticeHostContext=createContext<HTMLElement|null|undefined>(undefined);
type Props=HTMLAttributes<HTMLDivElement>&{tone?:NoticeTone;noticeKey?:string;actions?:ReactNode;layout?:'inline'|'screen';floating?:boolean;dismissKey?:string;onDismiss?:()=>void};

function noticeText(children:ReactNode):string{
 return Children.toArray(children).map(child=>isValidElement<{children?:ReactNode}>(child)?noticeText(child.props.children):String(child)).join('');
}

// Dismiss the message, not its draft/receipt. Stable content survives streaming
// rerenders; a different message or explicit draw context starts visible again.
export function Notice(props:Props){
 const host=useContext(NoticeHostContext);
 const policy=resolveNotification(useUi(),props.noticeKey,props.layout,props.tone);
 // Closed dialogs suspend every notice, including its expiry timer. Opening
 // them must not reveal an explanation whose five seconds elapsed off-screen.
 if(!policy.enabled||host===null)return null;
 const content=<NoticeContent key={JSON.stringify([props.noticeKey,props.dismissKey,props.noticeKey?undefined:noticeText(props.children)])} {...props} tone={policy.tone} layout={policy.layout} policy={policy}/>;
 return props.floating&&host?createPortal(content,host):content;
}

function NoticeContent({tone='info',layout='inline',floating,actions,children,className='',role='status',dismissKey,noticeKey,onDismiss,hidden,policy,...props}:Props&{policy:ReturnType<typeof resolveNotification>}){
 const t=useCopy(),[dismissed,setDismissed]=useState(false);
 useEffect(()=>{
  if(!policy.durationMs)return;
  // Expiry hides presentation only. Manual dismissal may discard a retained
  // selection, so that callback must never run just because time passed.
  const timer=setTimeout(()=>setDismissed(true),policy.durationMs);
  return()=>clearTimeout(timer);
 },[policy.durationMs]);
 return <div {...props} hidden={hidden||dismissed} role={role} aria-atomic="true" data-ui="room-notice" data-notice-id={noticeKey} data-dismissible={policy.dismissible} data-tone={tone} data-layout={layout} className={'room-notice '+className}>
  <svg className="room-notice-symbol" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
   {tone==='warning'?<><path d="M12 3 2 21h20Z"/><path d="M12 9v5m0 3h.01"/></>:<><circle cx="12" cy="12" r="9"/>{tone==='success'?<path d="m7 12 3 3 7-7"/>:tone==='error'?<path d="m9 9 6 6m0-6-6 6"/>:<path d="M12 11v6m0-10h.01"/>}</>}
  </svg>
  <div className="room-notice-message">{children}</div>
  {policy.dismissible&&<button type="button" className="room-notice-dismiss" aria-label={t('common.close')} onClick={()=>{setDismissed(true);onDismiss?.();}}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg></button>}
  {actions&&<div className="room-notice-actions">{actions}</div>}
 </div>;
}
